import {
  getBudgetDimensionWordLabels,
  getCatalogDimensionLabels,
  ui,
} from '@utils/i18n/uiCopy'

/** Las dimensiones internas de escena se almacenan en décimas de la unidad de catálogo (700 → 70). */
export const SCENE_TO_CATALOG_FACTOR = 10

/** Umbral para distinguir valores de menú ya en escala catálogo vs. décimas internas. */
const MENU_INTERNAL_DIMENSION_THRESHOLD = 300

export type CatalogDimensionKey = 'largo' | 'fondo' | 'alto'

export type CatalogDimensions = Record<CatalogDimensionKey, number | null>

export type SceneDimensionsInput = {
  width?: unknown
  height?: unknown
  thickness?: unknown
}

export const CATALOG_DIMENSION_ORDER: readonly CatalogDimensionKey[] = ['largo', 'fondo', 'alto']

export const CATALOG_DIMENSION_UNIT = 'cm'

export const CATALOG_DIMENSION_LABELS: Record<CatalogDimensionKey, string> = {
  largo: 'Largo',
  fondo: 'Fondo',
  alto: 'Alto',
}

export function parseNumericDimension(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value
  if (typeof value === 'string') {
    const trimmed = value.trim()
    if (!trimmed) return null
    const parsed = Number(trimmed.replace(',', '.'))
    if (Number.isFinite(parsed)) return parsed
  }
  return null
}

/** Convierte una dimensión interna de escena a entero de catálogo (700 → 70). */
export function sceneDimensionToCatalog(value: unknown): number | null {
  const parsed = parseNumericDimension(value)
  if (parsed == null) return null
  return Math.round(parsed / SCENE_TO_CATALOG_FACTOR)
}

/**
 * Normaliza dimensiones del menú/API que pueden llegar en escala catálogo (70)
 * o en décimas internas (700).
 */
export function normalizeMenuDimensionValue(value: unknown): number | null {
  const parsed = parseNumericDimension(value)
  if (parsed == null) return null
  if (parsed >= MENU_INTERNAL_DIMENSION_THRESHOLD) {
    return Math.round(parsed / SCENE_TO_CATALOG_FACTOR)
  }
  return Math.round(parsed)
}

export function sceneDimensionsToCatalog(input: SceneDimensionsInput): CatalogDimensions {
  return {
    largo: sceneDimensionToCatalog(input.width),
    fondo: sceneDimensionToCatalog(input.thickness),
    alto: sceneDimensionToCatalog(input.height),
  }
}

export function menuDimensionsToCatalog(input: {
  ancho?: unknown
  fondo?: unknown
  alto?: unknown
}): CatalogDimensions {
  return {
    largo: normalizeMenuDimensionValue(input.ancho),
    fondo: normalizeMenuDimensionValue(input.fondo),
    alto: normalizeMenuDimensionValue(input.alto),
  }
}

export function formatCatalogDimensionValue(value: number | null): string {
  return value != null ? `${value}${CATALOG_DIMENSION_UNIT}` : '-'
}

export function formatCatalogDimensionEntry(
  key: CatalogDimensionKey,
  dimensions: CatalogDimensions,
): string {
  const labels = getCatalogDimensionLabels()
  return `${labels[key]}: ${formatCatalogDimensionValue(dimensions[key])}`
}

export function formatCatalogDimensionsSuffix(dimensions: CatalogDimensions): string {
  return CATALOG_DIMENSION_ORDER.map((key) => formatCatalogDimensionEntry(key, dimensions)).join(
    ', ',
  )
}

function joinWithFinalConjunction(items: string[], conjunction = 'y'): string {
  if (items.length === 0) return ''
  if (items.length === 1) return items[0]
  if (items.length === 2) return `${items[0]} ${conjunction} ${items[1]}`
  return `${items.slice(0, -1).join(', ')} ${conjunction} ${items.at(-1)}`
}

export function formatBudgetDimensionEntry(
  key: CatalogDimensionKey,
  dimensions: CatalogDimensions,
): string {
  const labels = getBudgetDimensionWordLabels()
  return `${labels[key]}: ${formatCatalogDimensionValue(dimensions[key])}`
}

export function formatBudgetDimensionsSuffix(dimensions: CatalogDimensions): string {
  const entries = CATALOG_DIMENSION_ORDER.map((key) => formatBudgetDimensionEntry(key, dimensions))
  return joinWithFinalConjunction(entries, ui('and'))
}

export function formatBudgetReferenceLine(
  referencia: string,
  dimensions: CatalogDimensions,
): string {
  return `${referencia} (${formatBudgetDimensionsSuffix(dimensions)})`
}
